import { CodeScannerStandard } from "common/code-scanner-standards";
import sum from "lodash/sum";

import { ColorSpace } from "./locator/color-spaces";
import { getBarcodeLineFromImageDataLine, toBinaryLine } from "./barcode-line";
import { Barcode } from "./quagga2-readers/barcode-reader";
import NewCodabarReader from "./quagga2-readers/codabar-reader";
import Code128Reader from "./quagga2-readers/code-128-reader";
import Code39Reader from "./quagga2-readers/code-39-reader";
import Code93Reader from "./quagga2-readers/code-93-reader";
import EANReader from "./quagga2-readers/ean-reader";
import EAN2Reader from "./quagga2-readers/ean2-reader";
import EAN5Reader from "./quagga2-readers/ean5-reader";
import EAN8Reader from "./quagga2-readers/ean8-reader";
import I2of5Reader from "./quagga2-readers/i2of5-reader";
import UPCEReader from "./quagga2-readers/upc-e-reader";

export type DecodeParameters = Readonly<{
    maxQuantile: number;
    errorAtMaxQuantile: number;
    decodeStep: number;
    xPadding: number;
    onGrayscaleLine?: (line: number[]) => void;
    onBinaryLine?: (line: number[]) => void;
}>;

export interface DecodeResult {
    standard: CodeScannerStandard;
    code: string;
}

function undefinedIfNull(c: Barcode | null | undefined): Barcode | undefined {
    if (c === null) return undefined;
    return c;
}

const configs = { normalizeBarSpaceWidth: true };

const ean5Reader = new EAN5Reader(configs);
const ean2Reader = new EAN2Reader(configs);
const ean13Reader = new EANReader(configs, [ean5Reader, ean2Reader]);
const ean8Reader = new EAN8Reader(configs);
const upcEReader = new UPCEReader(configs);
const code39Reader = new Code39Reader(configs);
const code93Reader = new Code93Reader(configs);
const code128Reader = new Code128Reader(configs);
const i2of5Reader = new I2of5Reader(configs);
const codabarReader = new NewCodabarReader(configs);

const readers = [
    ean13Reader,
    // upcAReader, // EAN 13 is a superset of UPC A
    ean8Reader,
    upcEReader,
    code128Reader,
    code93Reader,
    // code39VINReader, Code 39 VIN is just a subset of Code 39
    code39Reader,
    i2of5Reader,
    codabarReader,
];

function errorsFromBarcode(barcode: Barcode): number[] {
    const ret: number[] = [];
    for (const decoded of barcode.decodedCodes ?? []) {
        if (typeof decoded === "string") continue;
        if (decoded.error !== undefined) {
            ret.push(decoded.error);
        }
    }
    return ret.sort((l, r) => l - r);
}

function decodeResultFromBarcode(c: Barcode | undefined): DecodeResult | undefined {
    if (c === undefined) return undefined;
    return {
        standard: c.format,
        code: c.code,
    };
}

export function decodeLine(line: number[], params: DecodeParameters): DecodeResult | undefined {
    const results: [number, Barcode][] = [];
    const { maxQuantile, errorAtMaxQuantile } = params;

    for (const reader of readers) {
        const result = undefinedIfNull(reader.decodePattern(line));
        if (result === undefined) continue;

        const errors = errorsFromBarcode(result);
        if (errors.length > 0) {
            const offset = Math.min(Math.floor(maxQuantile * errors.length), errors.length - 1);
            if (errors[offset] > errorAtMaxQuantile) {
                continue;
            }
        }

        const errorSum = sum(errors);
        results.push([errorSum, result]);
    }

    results.sort((l, r) => l[0] - r[0]);
    return decodeResultFromBarcode(results[0]?.[1]);
}

export function decodeBarcodeDirectImageDataFirstLine(
    imgData: ImageData,
    colorSpaces: ColorSpace[],
    params: DecodeParameters,
    yOffset: number
): DecodeResult | undefined {
    const { width } = imgData;
    const { onBinaryLine, onGrayscaleLine } = params;

    const firstColorSpace = colorSpaces[0];
    if (firstColorSpace === undefined) return undefined;

    let initialLine = getBarcodeLineFromImageDataLine(imgData, firstColorSpace, yOffset, 0, width);
    onGrayscaleLine?.(initialLine.line);
    let scanLine = toBinaryLine(initialLine);
    onBinaryLine?.(scanLine);

    let maybeResult = decodeLine(scanLine, params);
    if (maybeResult !== undefined) {
        return maybeResult;
    }

    for (const colorSpace of colorSpaces.slice(1)) {
        initialLine = getBarcodeLineFromImageDataLine(imgData, colorSpace, yOffset, 0, width);
        scanLine = toBinaryLine(initialLine);
        maybeResult = decodeLine(scanLine, params);
        if (maybeResult !== undefined) {
            return maybeResult;
        }
    }

    return undefined;
}

export function decodeBarcodeDirectImageData(
    imgData: ImageData,
    colorSpaces: ColorSpace[],
    params: DecodeParameters
): DecodeResult | undefined {
    const { width, height } = imgData;
    const ym = Math.floor(height / 2);
    const { decodeStep } = params;

    if (colorSpaces.length < 1) return undefined;

    let maybeResult = decodeBarcodeDirectImageDataFirstLine(imgData, colorSpaces, params, Math.floor(height / 2));
    if (maybeResult !== undefined) {
        return maybeResult;
    }

    for (let i = 0; i < height / 2; i += decodeStep) {
        for (const sign of [-1, 1]) {
            for (const colorSpace of colorSpaces) {
                const barcodeLine = getBarcodeLineFromImageDataLine(imgData, colorSpace, ym + sign * i, 0, width);
                const scanLine = toBinaryLine(barcodeLine);
                maybeResult = decodeLine(scanLine, params);
                if (maybeResult !== undefined) {
                    return maybeResult;
                }
            }
        }
    }
    return undefined;
}
